import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { PlanReturnView, TransferReturns } from '../../utils/transferReturns';

interface ComparisonAdviceProps {
	/** Rentabilidades y comisiones decididas por comparsa: la misma fuente que la tabla. */
	returns: TransferReturns & { arca: PlanReturnView };
	translate: (key: string) => string;
}

/**
 * Consejo de asesor dinámico según cómo salga la comparación de comisión y rentabilidad
 * NETA: el plan elegido gana pagando más, gana pagando menos, o el plan actual es mejor
 * (→ diversificar). Usa "este plan" (no "gestora", que es Caser). Sin datos del plan del
 * usuario, el consejo genérico.
 */
const ComparisonAdvice: React.FC<ComparisonAdviceProps> = ({ returns, translate }) => {
	const { arca, origin, samePeriod } = returns;
	const arcaReturn = arca.annualPct;
	const originReturn = origin?.annualPct ?? 0;
	// Sin las dos comisiones de gestión no se dice ni que cobra más ni que cobra menos.
	const feesKnown = arca.managementFeePct !== null && origin?.managementFeePct != null;
	const arcaCheaper = feesKnown && arca.managementFeePct! < origin!.managementFeePct!;

	const fmtPct = (n: number) => `${fmtNum(n, 2)}%`;

	// Sin datos del plan del usuario, o con periodos distintos (1 año frente a 5), no hay
	// comparación honesta: consejo genérico.
	const adviceCase =
		!origin || !samePeriod
			? 'generic'
			: arcaReturn <= originReturn
				? 'yoursBetter'
				: !feesKnown
					? 'betterReturn'
					: arcaCheaper
						? 'lowerCostBetter'
						: 'higherCostBetter';

	const highlight =
		translate(`individual.insight.${adviceCase}.highlight`) ||
		{
			higherCostBetter: 'En rentabilidad neta, sales ganando.',
			lowerCostBetter: 'Más rentabilidad neta y menos comisión.',
			yoursBetter: 'Tu plan actual es muy bueno.',
			betterReturn: 'En rentabilidad neta, este plan rinde más.',
			generic: 'Fíjate en la rentabilidad neta.'
		}[adviceCase];

	const body = (
		translate(`individual.insight.${adviceCase}.body`) ||
		{
			higherCostBetter:
				'Sí, este plan cobra algo más de comisión, pero lo que cuenta es la rentabilidad neta (ya con las comisiones descontadas): ha rendido {arcaReturn} frente al {originReturn} de tu plan actual. Aunque pagues algo más, rinde bastante mejor.',
			lowerCostBetter:
				'Este plan ha rendido más en neto ({arcaReturn} vs {originReturn}) y además cobra menos comisión. Con comisiones bajas, cuando el mercado flojea te quedas con una parte mayor de lo que rinde en vez de repartirla.',
			betterReturn:
				'Ya con las comisiones descontadas, este plan ha rendido más ({arcaReturn} vs {originReturn}) que tu plan actual.',
			yoursBetter:
				'En rentabilidad neta ha rendido más que este plan ({originReturn} vs {arcaReturn}). Aun así, concentrar todo en un solo plan es arriesgado: puedes probar otro plan de destino o traspasar solo una parte para diversificar tu cartera de pensiones.',
			generic:
				'La cifra que de verdad importa es la rentabilidad neta, ya con las comisiones descontadas. Es lo que acaba en tu bolsillo.'
		}[adviceCase]
	)
		.replace('{arcaReturn}', fmtPct(arcaReturn))
		.replace('{originReturn}', fmtPct(originReturn));

	return (
		<div className="rounded-xl border-l-4 border-primary bg-primary/5 p-4">
			<p className="text-sm leading-relaxed text-foreground/80">
				<strong className="font-semibold text-foreground">{highlight}</strong> {body}
			</p>
		</div>
	);
};

export default ComparisonAdvice;
